import type { FC, FormEvent, ReactNode } from 'react'
import type { ContactInput, FieldErrors } from '@/lib/validation'

import { useState } from 'react'
import { CircleCheck } from 'lucide-react'
import { validateContact } from '@/lib/validation'
import { focusFirstError, postJson } from '@/lib/submit'
import { Button } from '@/components/generals/button'
import { CONTACT_TOPICS } from '@/constants/forms'

import {
    FormAlert,
    Honeypot,
    SelectField,
    TextAreaField,
    TextField
} from '@/components/forms/fields'

const EMPTY: ContactInput = {
    name: '',
    email: '',
    company: '',
    topic: CONTACT_TOPICS[0],
    message: ''
}

const FIELD_ORDER: Array<keyof ContactInput> = ['name', 'email', 'company', 'topic', 'message']

export const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<ContactInput>(EMPTY)
    const [errors, setErrors] = useState<FieldErrors<keyof ContactInput>>({})
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
    const [message, setMessage] = useState<string | null>(null)
    const [website, setWebsite] = useState('')
    const [sentName, setSentName] = useState('')

    const update = (field: keyof ContactInput) => (value: string) => {
        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setMessage(null)

        const found = validateContact({
            ...values,
            name: values.name.trim(),
            email: values.email.trim(),
            message: values.message.trim()
        })

        if (Object.keys(found).length) {
            setErrors(found)
            focusFirstError('contact', FIELD_ORDER.filter(field => found[field]))

            return
        }

        setStatus('submitting')

        const result = await postJson('/api/contact', { ...values, website })

        if (result.ok) {
            setSentName(values.name.trim().split(' ')[0] ?? '')
            setValues(EMPTY)
            setErrors({})
            setStatus('success')

            return
        }

        setStatus('idle')
        setMessage(result.message ?? null)

        if (result.errors) setErrors(result.errors as FieldErrors<keyof ContactInput>)
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex min-h-[420px] flex-col items-start justify-center'>
                <span className='flex size-14 items-center justify-center rounded-full bg-sage/25 text-sage-dark'>
                    <CircleCheck aria-hidden='true' className='size-7' />
                </span>

                <h2 className='mt-8 font-heading text-display-sm font-medium text-burgundy'>
                    Thanks{sentName ? `, ${sentName}` : ''}. Your message is on its way.
                </h2>

                <p className='mt-4 max-w-md leading-relaxed text-stone-dark'>
                    A member of our team will reply within one business day. Keep an eye on your inbox, including the spam folder just in case.
                </p>

                <Button variant='secondary' className='mt-8' onClick={() => setStatus('idle')}>
                    Send another message
                </Button>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={onSubmit} className='relative space-y-6' aria-describedby={message ? 'contact-form-error' : undefined}>
            <Honeypot value={website} onChange={setWebsite} />

            {message && (
                <div id='contact-form-error'>
                    <FormAlert tone='error'>{message}</FormAlert>
                </div>
            )}

            <div className='grid gap-6 sm:grid-cols-2'>
                <TextField
                    id='contact-name'
                    label='Full name'
                    autoComplete='name'
                    value={values.name}
                    onChange={event => update('name')(event.target.value)}
                    error={errors.name}
                    required
                />

                <TextField
                    id='contact-email'
                    label='Work email'
                    type='email'
                    autoComplete='email'
                    inputMode='email'
                    value={values.email}
                    onChange={event => update('email')(event.target.value)}
                    error={errors.email}
                    required
                />
            </div>

            <div className='grid gap-6 sm:grid-cols-2'>
                <TextField
                    id='contact-company'
                    label='Company'
                    autoComplete='organization'
                    optional
                    value={values.company}
                    onChange={event => update('company')(event.target.value)}
                    error={errors.company}
                />

                <SelectField
                    id='contact-topic'
                    label='Topic'
                    options={CONTACT_TOPICS}
                    value={values.topic}
                    onChange={event => update('topic')(event.target.value)}
                    error={errors.topic}
                />
            </div>

            <TextAreaField
                id='contact-message'
                label='How can we help?'
                rows={6}
                value={values.message}
                onChange={event => update('message')(event.target.value)}
                error={errors.message}
                hint='Share as much context as you like. We read every message.'
                required
            />

            <div className='flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-xs leading-relaxed text-stone'>
                    By sending this form you agree that we may contact you about your enquiry.
                </p>

                <Button type='submit' size='lg' loading={status === 'submitting'} arrow className='shrink-0'>
                    {status === 'submitting' ? 'Sending' : 'Send message'}
                </Button>
            </div>
        </form>
    )
}
